import L from "leaflet";
import type { CatalogueEntry } from "../catalogue/types";
import type { LayerSource, SourceContext, SourceFactory } from "./types";

export type GoogleMutantFactory = (options: { type: string; maxZoom?: number; pane?: string; zIndex?: number }) => L.Layer;

export type GoogleMutantLoader = (apiKey: string) => Promise<GoogleMutantFactory | null>;

export const disabledReasons = {
  missingKey: "missing api key",
  missingPlugin: "google maps plugin is not available",
  unknownProvider: "unknown provider"
} as const;

type GridLayerNamespace = { googleMutant?: GoogleMutantFactory };

function installedGoogleMutant(): GoogleMutantFactory | null {
  const namespace = (L as unknown as { gridLayer: GridLayerNamespace }).gridLayer;

  return typeof namespace?.googleMutant === "function" ? namespace.googleMutant : null;
}

function loadScript(src: string): Promise<void> {
  return new Promise((resolve, reject) => {
    const existing = document.querySelector<HTMLScriptElement>(`script[src="${src}"]`);

    if (existing) {
      if (existing.dataset.loaded === "true") resolve();
      else {
        existing.addEventListener("load", () => resolve());
        existing.addEventListener("error", () => reject(new Error(src)));
      }

      return;
    }

    const script = document.createElement("script");

    script.src = src;
    script.async = true;
    script.addEventListener("load", () => {
      script.dataset.loaded = "true";
      resolve();
    });
    script.addEventListener("error", () => reject(new Error(src)));
    document.head.appendChild(script);
  });
}

export const defaultGoogleMutantLoader: GoogleMutantLoader = async apiKey => {
  const installed = installedGoogleMutant();

  if (installed !== null) return installed;

  if (typeof document === "undefined") return null;

  try {
    await loadScript(`https://maps.googleapis.com/maps/api/js?key=${encodeURIComponent(apiKey)}&loading=async`);
    await loadScript("https://unpkg.com/leaflet.gridlayer.googlemutant@0.14.1/dist/Leaflet.GoogleMutant.js");
  } catch {
    return null;
  }

  return installedGoogleMutant();
};

export function quadKey(x: number, y: number, zoom: number): string {
  let key = "";

  for (let index = zoom; index > 0; index--) {
    let digit = 0;
    const mask = 1 << (index - 1);

    if ((x & mask) !== 0) digit += 1;
    if ((y & mask) !== 0) digit += 2;

    key += String(digit);
  }

  return key;
}

export function bingVariant(variant: string | null): string {
  switch ((variant ?? "").toLowerCase()) {
    case "aerial":
    case "a":
      return "a";
    case "hybrid":
    case "aerialwithlabels":
    case "h":
      return "h";
    default:
      return "r";
  }
}

export class BingTileLayer extends L.TileLayer {
  private readonly variant: string;

  constructor(variant: string | null, options: L.TileLayerOptions) {
    super("", options);
    this.variant = bingVariant(variant);
  }

  override getTileUrl(coords: L.Coords): string {
    const subdomain = ["0", "1", "2", "3"][(coords.x + coords.y) % 4];

    return `https://ecn.t${subdomain}.tiles.virtualearth.net/tiles/${this.variant}${quadKey(coords.x, coords.y, coords.z)}.jpeg?g=1&n=z`;
  }
}

function disabledSource(entry: CatalogueEntry, pane: string, reason: string): LayerSource {
  const layer = L.layerGroup([], { pane });

  return {
    entry,
    kind: "special",
    layer,
    pane,
    bounds: () => null,
    features: null,
    info: null,
    legend: () => entry.legend,
    disabledReason: reason,
    dispose: () => layer.remove()
  };
}

function googleSource(entry: CatalogueEntry, context: SourceContext, pane: string, loader: GoogleMutantLoader): LayerSource {
  const special = entry.special!;
  const apiKey = special.apiKey;

  if (!apiKey) return disabledSource(entry, pane, disabledReasons.missingKey);

  const group = L.layerGroup([], { pane });
  let mutant: L.Layer | null = null;
  let disposed = false;
  const source: LayerSource = {
    entry,
    kind: "special",
    layer: group,
    pane,
    bounds: () => null,
    features: null,
    info: null,
    legend: () => entry.legend,
    disabledReason: null,
    setOpacity: value => {
      const pane = context.map.getPane(source.pane ?? "");

      if (pane) pane.style.opacity = String(value);
    },
    dispose: () => {
      disposed = true;
      group.clearLayers();
      group.remove();
    }
  };

  loader(apiKey)
    .then(factory => {
      if (disposed) return;

      if (factory === null) {
        source.disabledReason = disabledReasons.missingPlugin;
        context.emitError(entry, null, disabledReasons.missingPlugin);
        return;
      }

      mutant = factory({ type: special.variant ?? "roadmap", maxZoom: 21, pane, zIndex: context.zIndexFor(entry) });
      group.addLayer(mutant);
    })
    .catch(() => {
      if (disposed) return;

      source.disabledReason = disabledReasons.missingPlugin;
      context.emitError(entry, null, disabledReasons.missingPlugin);
    });

  return source;
}

export function createSpecialSource(loader: GoogleMutantLoader = defaultGoogleMutantLoader): SourceFactory {
  return (entry, context) => {
    const special = entry.special;

    if (special === null) return null;

    const pane = context.paneFor(entry);

    if (special.provider === "google") return googleSource(entry, context, pane, loader);

    if (special.provider === "bing") {
      const layer = new BingTileLayer(special.variant, { attribution: special.attribution, maxZoom: 19, pane, zIndex: context.zIndexFor(entry) });

      return {
        entry,
        kind: "special",
        layer,
        pane,
        bounds: () => null,
        features: null,
        info: null,
        legend: () => entry.legend,
        disabledReason: null,
        setOpacity: value => layer.setOpacity(value),
        dispose: () => layer.remove()
      };
    }

    return disabledSource(entry, pane, `${disabledReasons.unknownProvider}: ${special.provider}`);
  };
}

export const specialSource: SourceFactory = createSpecialSource();
